import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from "@angular/core";

import { Subject } from "rxjs";

import { DisposeMixin, EmptyClassInstance } from "../shared-library/mixins/dispose.mixin";
import { ExternalConfigurationService } from "../shared-library/services/configuration/external-configuration.service";
import { BrowserWindowService } from "../shared-library/services/environment/browser-window.service";
import { QueryStringSerializationService } from "../shared-library/services/environment/query-string-serialization.service";

import { BaseSearchParametersModel } from "./models/base-search-parameters.model";
import { BaseSearchTabConfigModel } from "./models/base-search-tab-config.model";
import { SearchPageConfigModel } from "./models/search-page-config.model";
import { SearchTabAlias } from "./enums/search-tab-alias.enum";

declare function externalUpdateActiveSearchTabFn(tabAlias: string): any;

@Component({
    selector: "pcg-ng-advanced-search",
    template: require('./advanced-search.component.html')
})
export class AdvancedSearchComponent extends DisposeMixin(EmptyClassInstance) implements OnInit, AfterViewInit, OnDestroy {
    private _activeTabConfig: BaseSearchTabConfigModel = null;
    private tabAlias = SearchTabAlias;

    public searchPageConfig: Array<BaseSearchTabConfigModel>;
    public activeTabChangedEvent: Subject<BaseSearchTabConfigModel> = new Subject<BaseSearchTabConfigModel>();

    public get isSingleTab(): boolean { return this.searchPageConfig.length === 1; }

    public constructor(
        private _domElement: ElementRef,
        private _configurationService: ExternalConfigurationService<SearchPageConfigModel>,
        private _browserWindowService: BrowserWindowService,
        private _serializationService: QueryStringSerializationService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this._configurationService.initialize(this._domElement);
        this.searchPageConfig = this._configurationService.config.searchPageConfig;
        this.setActiveTab();
    }

    public ngAfterViewInit(): void {
        setTimeout(() => {
            const activeTab = this.searchPageConfig.find(searchTab => searchTab.isActive);
            this.activeTabChangedEvent.next(activeTab);
        });
    }

    public ngOnDestroy(): void {
        this.dispose();
    }

    public onTabClicked(event: Event, activeTab: BaseSearchTabConfigModel): void {
        event.preventDefault();

        this.changeActiveTab(activeTab);
    }

    public onActiveTabChanged(activeTab: BaseSearchTabConfigModel): void {
        this.changeActiveTab(activeTab);
    }

    private changeActiveTab(activeTab: BaseSearchTabConfigModel): void {
        this.searchPageConfig.forEach(searchTab => {
            if (searchTab.isActive) {
                searchTab.wasActive = true;
            }
            searchTab.isActive = false;
        });

        activeTab.isActive = true;
        this._activeTabConfig = activeTab;
        this.activeTabChangedEvent.next(activeTab);
        externalUpdateActiveSearchTabFn(activeTab.tabAlias);
    }

    private setActiveTab(): void {
        if (this.isSingleTab) {
            this._activeTabConfig = this.searchPageConfig[0];
        }

        const searchString = this._browserWindowService.getQueryString();
        const searchParams = this._serializationService.deserialize<BaseSearchParametersModel>(searchString);

        const activeTabAlias = searchParams.searchTab;

        if (activeTabAlias) {
            this._activeTabConfig = this.searchPageConfig.find(tabConfig => tabConfig.tabAlias === activeTabAlias);
        } else {
            this._activeTabConfig = this.searchPageConfig.find(tabConfig => tabConfig.tabAlias === this.tabAlias.CONTENT_SEARCH);
        }

        if (!this._activeTabConfig) {
            this._activeTabConfig = this.searchPageConfig.find(tabConfig => tabConfig.isActive);
        }

        this.searchPageConfig.forEach(tabConfig => tabConfig.isActive = false);

        if (this._activeTabConfig) {
            this._activeTabConfig.isActive = true;
            this.activeTabChangedEvent.next(this._activeTabConfig);
        }
    }

    private getSearchText(): string {
        const queryParams = new URLSearchParams(window.location.search);

        return queryParams.get("searchText") || "";
    }
}
